import { yupResolver } from '@hookform/resolvers/yup';
import SubmitButton from 'components/form/button/SubmitButton';
import ModalWrapper, { ModalFooter } from 'components/ui/modal/ModalWrapper';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { buttons } from 'utils/resources';
import T from 'utils/useLocalizer';
import { TEMPLATE_HEADER_FILE_MAX_SIZE } from 'utils/setting';
import { ISendInvoiceFormData } from 'models/ordersModel';
import { ceil } from 'lodash';
import { getBase64, getFileExt, stringReplace } from 'utils/utils';
import styles from '../style.module.scss';
import { Paperclip } from 'react-bootstrap-icons';

interface Props {
    orderNumber: string;
    show: boolean;
    isLoading: boolean;
    onSubmit: (data: ISendInvoiceFormData) => void
    onClose: () => any;
}

const sendInvoiceSchema = yup.object({
    invoiceFile: yup.object({
        file: yup.mixed<File>().nullable(),
        name: yup.string().required(),
        url: yup.string().required('order.invoice.validation.document.required'),
        ext: yup.string().required(),
        size: yup.number().max(TEMPLATE_HEADER_FILE_MAX_SIZE, 'order.invoice.validation.document.max.size'),
    })
        .test('fileType', 'order.invoice.validation.document.invalid.type', (value) => {
            if (!value || !value.file) return false;
            const file = value.file;;
            const isPdfFile = file.name.endsWith('.pdf');
            return isPdfFile;
        })
});

function OrderSendInvoiceModal({ orderNumber, show, isLoading, onSubmit, onClose }: Props) {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const { setValue, reset, watch, handleSubmit, formState: { errors, isValid }, } = useForm<ISendInvoiceFormData>({
        resolver: yupResolver(sendInvoiceSchema),
        defaultValues: {
            invoiceFile: { file: null, name: '', url: '', ext: '', size: 0 },
        },
    });

    const selectedFile = watch('invoiceFile');

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!event.target.files || event.target.files.length === 0) {
            return;
        }
        const file = event.target.files?.[0] ?? undefined;

        const fileBase64 = await getBase64(file);
        setValue('invoiceFile', {
            file: file,
            name: file.name,
            url: fileBase64,
            ext: getFileExt(file),
            size: file.size
        }, { shouldValidate: true });
    };

    const handleCancel = () => {
        reset();
        onClose();
    }

    const handleFormSubmit = (data: ISendInvoiceFormData) => {
        onSubmit(data);
        reset();
    }

    const resources = {
        submitButton: T(buttons.submit),
        cancelButton: T(buttons.cancel),
        attachButton: T(buttons.attachFile),
        modalTitle: T('order.invoice.send.modal.title'),
        uploadingLabel: T('order.invoice.send.uploading.label'),
        fileValidationMessage: T(errors.invoiceFile?.message || ''),
        fileSizeValidationMessage: T(errors.invoiceFile?.size?.message || '')
    }

    return (
        <ModalWrapper
            title={stringReplace(resources.modalTitle, [orderNumber])}
            show={show}
            closeButton
            onHide={handleCancel}
        >
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <div className='d-flex flex-column gap-2'>
                    <>
                        <input
                            ref={inputRef}
                            type='file'
                            accept='.pdf'
                            className='d-none'
                            id='invoice-upload'
                            onChange={handleFileChange}
                            disabled={isLoading}
                        />

                        <label
                            htmlFor='invoice-upload'
                            className={`btn me-3 ${styles.uploadInvoiceButton} text-muted py-4 ${isLoading ? 'disabled' : ''}`}
                        >
                            {selectedFile.name ? (
                                <div className='small'>
                                    {selectedFile.name}
                                </div>
                            ) : isLoading
                                ? resources.uploadingLabel
                                : <>
                                    <Paperclip className='me-2' />
                                    {resources.attachButton}
                                </>}
                        </label>
                    </>

                    {errors.invoiceFile ?
                        errors.invoiceFile.size ? (
                            <div className='text-danger small'>
                                {stringReplace(resources.fileSizeValidationMessage, [ceil(TEMPLATE_HEADER_FILE_MAX_SIZE / (1024 * 1024)).toString()])}
                            </div>
                        ) : (
                            <div className='text-danger small'>
                                {resources.fileValidationMessage}
                            </div>
                        ) : null}
                </div>

                <ModalFooter className='border-0'>
                    <button type='button' className='btn btn-secondary ms-3' onClick={handleCancel}>
                        {resources.cancelButton}
                    </button>
                    <SubmitButton label={resources.submitButton} isLoading={isLoading} disabled={!isValid || isLoading} />
                </ModalFooter>
            </form>
        </ModalWrapper>
    )
}

export default OrderSendInvoiceModal